import { useEffect, useState } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { LatestUpdateCard } from './LatestUpdateCard';
import type { LatestUpdateItem } from '../../hooks/useLatestUpdates';

interface LatestUpdatesCarouselProps {
  updates: LatestUpdateItem[];
}

const AUTOPLAY_MS = 10_000;

export const LatestUpdatesCarousel = ({ updates }: LatestUpdatesCarouselProps) => {
  const [loaded, setLoaded] = useState(false);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      loop: true,
      slides: {
        perView: 1,
        spacing: 16,
      },
      breakpoints: {
        '(min-width: 768px)': {
          slides: { perView: 2, spacing: 16 },
        },
        '(min-width: 1024px)': {
          slides: { perView: 3, spacing: 16 },
        },
      },
      created(slider) {
        setLoaded(true);
        requestAnimationFrame(() => {
          slider.update();
        });
      },
    },
    [
      (slider) => {
        let timeout: ReturnType<typeof setTimeout>;
        let mouseOver = false;

        const clearNextTimeout = () => {
          clearTimeout(timeout);
        };

        const nextTimeout = () => {
          clearTimeout(timeout);
          if (mouseOver) return;
          timeout = setTimeout(() => {
            slider.next();
          }, AUTOPLAY_MS);
        };

        slider.on('created', () => {
          slider.container.addEventListener('mouseover', () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener('mouseout', () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });
        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);
      },
    ],
  );

  useEffect(() => {
    instanceRef.current?.update();
  }, [updates, instanceRef, loaded]);

  return (
    <div className="relative max-lg:pr-10">
      <div ref={sliderRef} className="keen-slider overflow-hidden">
        {updates.map((update) => (
          <div key={update.documentId} className="keen-slider__slide">
            <LatestUpdateCard update={update} />
          </div>
        ))}
      </div>

      {loaded ? (
        <button
          type="button"
          aria-label="Next updates"
          className="absolute right-0 top-1/2 z-20 -translate-y-1/2 text-people-coral lg:-right-8"
          onClick={() => instanceRef.current?.next()}>
          <ChevronRightIcon className="size-6" />
        </button>
      ) : null}
    </div>
  );
};
